"use client";

import { useState } from "react";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";

/**
 * STORY-1717b Part 1 — user-facing FX-spread disclosure on the deposit
 * and withdraw flows. Surfaces the platform's 0.50% spread inline with
 * the rate the user actually pays/receives, so the BRL number on the
 * confirmation screen and the BRL number the bank actually moves are
 * the same thing — not "mid-market R$5,00 you see, R$5,03 you pay".
 *
 * Founder-locked design (2026-05-10):
 *   - Q1 — Polymarket combined as primary view, with an ⓘ See breakdown
 *     expand for the Bet365-itemized split. Persona-friendly default,
 *     power-user transparency one click away.
 *   - Q2 — per-pair defaults: BRL 50 bps (current flat rate). MXN 50 /
 *     CAD 30 / INR 75 land with STORY-1717b Part 2 (per-pair keys).
 *     This component reads `spreadBps` from props so the caller can
 *     swap default-50 for fetched-per-pair later without an API change.
 *   - Q3 — 7-day legacy-key fallback during the Part-2 migration.
 *
 * Suppression: returns null when the user's displayCurrency is USDC.
 * USDC-primary users never see a BRL spread because they don't transact
 * through the BRL leg — they hold USDC end-to-end on a crypto-direct
 * deposit/withdrawal path. Showing them a BRL spread would be noise.
 */
export type FxSpreadDirection = "deposit" | "withdraw";

interface FxSpreadDisclosureProps {
  direction: FxSpreadDirection;
  /** Override the default 50 bps. STORY-1717b Part 2 replaces the
   *  default with a per-pair lookup. */
  spreadBps?: number;
}

const DEFAULT_SPREAD_BPS = 50;

export function FxSpreadDisclosure({
  direction,
  spreadBps = DEFAULT_SPREAD_BPS,
}: FxSpreadDisclosureProps) {
  const displayCurrency = useDisplayCurrency();
  const fx = useFxRate("BRL");
  const [expanded, setExpanded] = useState(false);

  // USDC-primary users transact USDC end-to-end; no BRL spread applies.
  if (displayCurrency !== "BRL") return null;
  // If FX isn't available yet, render nothing rather than a misleading
  // partial rate. The full deposit/withdraw flow already has its own
  // FX-unavailable affordance upstream.
  if (!fx) return null;

  // `fx.rate` is BRL per USDC mid-market. Spread is applied per direction:
  //   deposit  — user pays extra BRL per USDC (worse for the user)
  //   withdraw — user receives less BRL per USDC (worse for the user)
  // Both directions are "worse for the user" by 0.50% so the platform
  // captures the spread on both legs.
  const spreadFraction = spreadBps / 10000;
  const midRateBrlPerUsdc = fx.rate;
  const userRateBrlPerUsdc =
    direction === "deposit"
      ? midRateBrlPerUsdc * (1 + spreadFraction)
      : midRateBrlPerUsdc * (1 - spreadFraction);
  const spreadBrlPerUsdc = Math.abs(userRateBrlPerUsdc - midRateBrlPerUsdc);

  const fmt = (n: number) =>
    n.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });

  const spreadPctLabel = (spreadBps / 100).toFixed(2);

  return (
    <div
      className="my-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-[12px] text-[var(--color-text2)]"
      data-testid="fx-spread-disclosure"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[var(--color-text3)]">
          {direction === "deposit" ? "Câmbio de depósito" : "Câmbio de saque"}
        </span>
        <span className="font-mono font-semibold text-[var(--color-text)]">
          1 USDC = {fmt(userRateBrlPerUsdc)}
        </span>
      </div>
      <div className="mt-0.5 text-[10px] text-[var(--color-text3)]">
        Inclui {spreadPctLabel}% de spread Granbay sobre a taxa mid-market do
        dia.
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-[var(--color-brand)] hover:underline"
        aria-expanded={expanded}
        aria-controls="fx-spread-breakdown"
      >
        <span aria-hidden>ⓘ</span>
        {expanded ? "Esconder detalhamento" : "Ver detalhamento"}
      </button>
      {expanded && (
        <dl
          id="fx-spread-breakdown"
          className="mt-2 grid grid-cols-2 gap-y-0.5 border-t border-[var(--color-border)] pt-2 text-[11px] tabular-nums"
        >
          <dt className="text-[var(--color-text3)]">Taxa mid-market</dt>
          <dd className="text-right font-mono">{fmt(midRateBrlPerUsdc)}</dd>
          <dt className="text-[var(--color-text3)]">
            Spread Granbay ({spreadPctLabel}%)
          </dt>
          <dd className="text-right font-mono">
            {direction === "deposit" ? "+" : "−"}
            {fmt(spreadBrlPerUsdc)}
          </dd>
          <dt className="font-semibold text-[var(--color-text2)]">Sua taxa</dt>
          <dd className="text-right font-mono font-semibold text-[var(--color-text)]">
            {fmt(userRateBrlPerUsdc)}
          </dd>
          <dt className="col-span-2 mt-1 text-[10px] text-[var(--color-text3)]">
            Saiba mais em{" "}
            <a
              href="/help/fx-spread"
              className="text-[var(--color-brand)] hover:underline"
            >
              /help/fx-spread
            </a>
            .
          </dt>
        </dl>
      )}
    </div>
  );
}
